/**
 * Constantes de fréquences — Leu Budget du Génie
 * ================================================
 * 
 * Ce fichier centralise toutes les options de fréquence utilisées
 * dans l'application. Il doit être importé par TOUS les composants
 * qui gèrent des récurrences.
 * 
 * Version: 1.0.0
 * Date: Avril 2026
 * 
 * MODULES CONCERNÉS:
 * - RecurringOperationForm (revenus/dépenses)
 * - CreateSavingsGoalWizard (objectifs)
 * - CreateSavingsGoalDialog (objectifs)
 * - Crédits
 * - Portefeuilles
 */

import { 
  Calendar, 
  CalendarDays, 
  CalendarRange,
  Clock,
  Repeat,
  RotateCcw,
  Zap,
  Settings2
} from 'lucide-react';

/**
 * FRÉQUENCES STANDARD
 * 
 * Chaque fréquence définit:
 * - value: identifiant technique (envoyé au backend)
 * - label: libellé affiché à l'utilisateur
 * - shortLabel: libellé court pour les espaces réduits
 * - description: explication détaillée
 * - unit: unité de base (jours, semaines, mois, années)
 * - unitValue: nombre d'unités entre chaque occurrence
 * - multiplierToMonthly: coefficient pour convertir en équivalent mensuel
 * - icon: icône Lucide associée
 * - requiresDayOfMonth: si true, demande le jour du mois
 * - requiresCustomInput: si true, affiche les champs personnalisés
 */
export const FREQUENCIES = [
  {
    value: 'unique',
    label: 'Ponctuel (une seule fois)',
    shortLabel: 'Ponctuel',
    description: 'Opération unique, sans répétition',
    unit: null,
    unitValue: 0,
    multiplierToMonthly: 0,
    icon: Clock,
    requiresDayOfMonth: false,
    requiresCustomInput: false,
  },
  {
    value: 'quotidien',
    label: 'Quotidien',
    shortLabel: 'Quotidien',
    description: 'Tous les jours',
    unit: 'jours',
    unitValue: 1,
    multiplierToMonthly: 30, // ~30 jours/mois
    icon: RotateCcw,
    requiresDayOfMonth: false,
    requiresCustomInput: false,
  },
  {
    value: 'hebdomadaire',
    label: 'Hebdomadaire',
    shortLabel: 'Hebdo',
    description: 'Chaque semaine (tous les 7 jours)',
    unit: 'semaines',
    unitValue: 1,
    multiplierToMonthly: 4.33, // ~4.33 semaines/mois
    icon: Zap,
    requiresDayOfMonth: false,
    requiresCustomInput: false,
  },
  {
    value: 'quinzaine',
    label: 'Bi-hebdomadaire',
    shortLabel: 'Bi-hebdo',
    description: 'Tous les 15 jours (2 fois par mois)',
    unit: 'jours',
    unitValue: 15,
    multiplierToMonthly: 2, // 2 fois par mois
    icon: Repeat,
    requiresDayOfMonth: false,
    requiresCustomInput: false,
  },
  {
    value: 'mensuel',
    label: 'Mensuel',
    shortLabel: 'Mensuel',
    description: 'Chaque mois à date fixe',
    unit: 'mois',
    unitValue: 1,
    multiplierToMonthly: 1,
    icon: Calendar,
    requiresDayOfMonth: true,
    requiresCustomInput: false,
  },
  {
    value: 'bimestriel',
    label: 'Bimestriel',
    shortLabel: 'Bimestriel',
    description: 'Tous les 2 mois',
    unit: 'mois',
    unitValue: 2,
    multiplierToMonthly: 0.5, // 0.5 fois par mois
    icon: CalendarDays,
    requiresDayOfMonth: true,
    requiresCustomInput: false,
  },
  {
    value: 'trimestriel',
    label: 'Trimestriel',
    shortLabel: 'Trimestriel',
    description: 'Tous les 3 mois (4 fois par an)',
    unit: 'mois',
    unitValue: 3,
    multiplierToMonthly: 0.33, // ~0.33 fois par mois
    icon: CalendarRange,
    requiresDayOfMonth: true,
    requiresCustomInput: false,
  },
  {
    value: 'semestriel',
    label: 'Semestriel',
    shortLabel: 'Semestriel',
    description: 'Tous les 6 mois (2 fois par an)',
    unit: 'mois',
    unitValue: 6,
    multiplierToMonthly: 0.167, // ~0.167 fois par mois
    icon: CalendarRange,
    requiresDayOfMonth: true,
    requiresCustomInput: false,
  },
  {
    value: 'annuel',
    label: 'Annuel',
    shortLabel: 'Annuel',
    description: 'Une fois par an',
    unit: 'mois',
    unitValue: 12,
    multiplierToMonthly: 0.083, // ~0.083 fois par mois
    icon: CalendarRange,
    requiresDayOfMonth: true,
    requiresCustomInput: false,
  },
  {
    value: 'personnalise',
    label: 'Personnalisé',
    shortLabel: 'Perso',
    description: 'Définir une fréquence sur mesure',
    unit: null, // Défini par l'utilisateur
    unitValue: null, // Défini par l'utilisateur
    multiplierToMonthly: null, // Calculé dynamiquement
    icon: Settings2,
    requiresDayOfMonth: false, // Dépend de l'unité choisie
    requiresCustomInput: true,
  },
];

/**
 * UNITÉS PERSONNALISÉES
 * 
 * Options disponibles pour la fréquence personnalisée.
 */
export const CUSTOM_FREQUENCY_UNITS = [
  { value: 'jours', label: 'jour(s)', multiplierBase: 30 },
  { value: 'semaines', label: 'semaine(s)', multiplierBase: 4.33 },
  { value: 'mois', label: 'mois', multiplierBase: 1 },
  { value: 'annees', label: 'année(s)', multiplierBase: 0.083 },
];

/**
 * HELPERS
 */

// Trouver une fréquence par sa valeur
export const getFrequencyByValue = (value) => {
  return FREQUENCIES.find(f => f.value === value) || null;
};

// Calculer le multiplicateur mensuel pour une fréquence personnalisée
export const calculateCustomMultiplier = (unit, value) => {
  const unitConfig = CUSTOM_FREQUENCY_UNITS.find(u => u.value === unit);
  if (!unitConfig || !value || value <= 0) return 0;
  
  // Nombre d'occurrences par mois = base / valeur
  // Ex: tous les 2 jours = 30 / 2 = 15 fois par mois
  // Ex: toutes les 2 semaines = 4.33 / 2 = 2.17 fois par mois
  return unitConfig.multiplierBase / value;
};

// Obtenir le libellé descriptif d'une fréquence
export const getFrequencyLabel = (frequencyType, frequencyUnit, frequencyValue) => {
  if (frequencyType === 'personnalise' && frequencyUnit && frequencyValue) {
    const unitLabel = CUSTOM_FREQUENCY_UNITS.find(u => u.value === frequencyUnit)?.label || frequencyUnit;
    return `Tous les ${frequencyValue} ${unitLabel}`;
  }
  
  const freq = getFrequencyByValue(frequencyType);
  return freq ? freq.label : frequencyType;
};

// Vérifier si une fréquence nécessite le jour du mois
export const requiresDayOfMonth = (frequencyType, frequencyUnit) => {
  if (frequencyType === 'personnalise') {
    return frequencyUnit === 'mois' || frequencyUnit === 'annees';
  }
  
  const freq = getFrequencyByValue(frequencyType);
  return freq ? freq.requiresDayOfMonth : false;
};

// Fréquences pour les revenus/dépenses (toutes)
export const REVENUE_EXPENSE_FREQUENCIES = FREQUENCIES;

// Fréquences pour les objectifs (sans unique et quotidien)
export const GOAL_FREQUENCIES = FREQUENCIES.filter(f => 
  !['unique', 'quotidien'].includes(f.value)
);

// Fréquences pour les crédits (mensuel et plus)
export const CREDIT_FREQUENCIES = FREQUENCIES.filter(f => 
  ['mensuel', 'bimestriel', 'trimestriel', 'semestriel', 'annuel', 'personnalise'].includes(f.value)
);

export default FREQUENCIES;
